import { StemType, TransportStates } from '@arpeggi/shared'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import ReactGA from 'react-ga'
import { useLocation } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { useTreeState } from '../../../stores/Root'
import { getMultipleSampleAudioBuffers } from '../../../stores/song/sample/SampleUtils'
import { IDrumStemStore } from '../../../stores/song/stem/DrumStemStore'
import { IInstrumentStemStore } from '../../../stores/song/stem/InstrumentStemStore'
import { IStemStore } from '../../../stores/song/StemStore'
import { CurrentlyPlayingTypes } from '../../studio/Studio'

export const SELECTED_CONTAINER_MIN_HEIGHT = 300

const useStudio = () => {
  useLayoutEffect(() => {
    window.document.body.style.setProperty('overflow', 'hidden')
    return () => {
      window.document.body.style.removeProperty('overflow')
    }
  }, [])
  const { studio, alternatePlayer, player } = useTreeState()
  const [sideDrawerContent, setSideDrawerContent] = React.useState(null)
  const [selectedActivityBarIcon, setSelectedActivityBarIcon] = React.useState(null)
  const [instrumentLoopWidthPX, setInstrumentLoopWidthPX] = React.useState(170)
  const [clipboard, setClipboard] = React.useState({ type: null, value: null })
  const [selectedTrackContainerHeight, setSelectedTrackContainerHeight] = useState<string>('45vh')
  const [lastSelectedTrackContainerHeight, setLastSelectedTrackContainerHeight] =
    useState<string>('45vh')
  const [currentlyPlaying, setCurrentlyPlaying] = React.useState({
    type: null,
    index: -1
  })
  const location = useLocation()

  useEffect(() => {
    const selectedStem = studio.song.stems[studio.selectedStemIndex]
    if (!selectedStem) return

    const selectedLoop = selectedStem.getLoopAtSectionOrNull(studio.selectedSection)
    if (!selectedLoop && selectedStem.type() !== StemType.AUDIO) {
      setSelectedTrackContainerHeight(`${SELECTED_CONTAINER_MIN_HEIGHT}px`)
    } else {
      setSelectedTrackContainerHeight(lastSelectedTrackContainerHeight)
    }
  }, [
    studio.selectedStemIndex,
    studio.selectedSection,
    studio.song.stems[studio.selectedStemIndex]?.getLoopAtSectionOrNull(studio.selectedSection)
  ])

  useEffect(() => {
    if (selectedTrackContainerHeight !== `${SELECTED_CONTAINER_MIN_HEIGHT}px`) {
      setLastSelectedTrackContainerHeight(selectedTrackContainerHeight)
    }
  }, [selectedTrackContainerHeight])

  const handleSelectedContainerResize = (newSize: number) => {
    setSelectedTrackContainerHeight(
      Math.min(document.body.clientHeight, Math.max(newSize, SELECTED_CONTAINER_MIN_HEIGHT)) + 'px'
    )
  }

  // if the user makes the selected track container large then resizes the screen,
  // we need to prevent it from hiding the resize handle above the top of the screen
  useEffect(() => {
    const handleScreenResize = () => {
      const selectedTrackContainerHeightPx = Number(
        selectedTrackContainerHeight.substring(0, selectedTrackContainerHeight.length - 2)
      )
      if (selectedTrackContainerHeightPx >= window.innerHeight)
        handleSelectedContainerResize(window.innerHeight - 5)
    }

    window.addEventListener('resize', handleScreenResize)
    return () => window.removeEventListener('resize', handleScreenResize)
  }, [selectedTrackContainerHeight])

  const handleHotkeys = (keyName, e, handle) => {
    e.preventDefault()
    if (keyName === 'space') {
      if (studio.transportState !== TransportStates.PLAYING) studio.play()
      else studio.stop()
    } else e.preventDefault()
    if (keyName === 'enter') {
      studio.stop()
    }
    if (keyName === 'control+z' || keyName === 'command+z') {
      try {
        studio.undo()
      } catch (e) {
        console.log(e)
      }
    }
    if (
      keyName === 'shift+control+z' ||
      keyName === 'shift+command+z' ||
      keyName === 'control+y' ||
      keyName === 'command+y'
    ) {
      try {
        studio.redo()
      } catch (e) {
        console.log(e)
      }
    }
    if (keyName === 'delete') {
      if (studio.selectedClipIDs.length > 0) studio.deleteSelectedClips()
      else studio.removeStem(studio.selectedStemIndex)
    }
  }

  const closeSideDrawer = () => {
    studio.setSidebarOpen(false)
    setSideDrawerContent(null)
    setSelectedActivityBarIcon(null)
  }

  const pullOutSideDrawer = (component, title) => {
    if (title === selectedActivityBarIcon) closeSideDrawer()
    else {
      ReactGA.event({
        category: 'Studio',
        action: 'Opened Drawer: ' + title
      })
      studio.setSidebarOpen(true)
      setSideDrawerContent(component)
      setSelectedActivityBarIcon(title)
    }
  }

  const zoomOut = () => {
    // this logic maintains that the center of the track table stays there after zooming out
    const trackStems = document.getElementById('track-stems')
    const width = trackStems.getBoundingClientRect().width
    const currentLeft = trackStems.scrollLeft
    const newWidth = Math.max(62.5, instrumentLoopWidthPX * 0.75)
    const ratio = newWidth / instrumentLoopWidthPX
    setInstrumentLoopWidthPX(newWidth)
    trackStems.scrollTo({ left: currentLeft * ratio + (width / 2) * (ratio - 1) })
  }

  const zoomIn = () => {
    // this logic maintains that the center of the track table stays there after zooming in
    const trackStems = document.getElementById('track-stems') as HTMLDivElement
    const width = trackStems.getBoundingClientRect().width
    const currentLeft = trackStems.scrollLeft
    const newWidth = Math.min(1000, instrumentLoopWidthPX * 1.25)
    const ratio = newWidth / instrumentLoopWidthPX
    setInstrumentLoopWidthPX(newWidth)
    trackStems.scrollTo({ left: currentLeft * ratio + (width / 2) * (ratio - 1) })
  }

  // for listening to stems/songs in the side drawer
  const updateCurrentlyPlaying = useCallback(
    (dataType, data) => {
      alternatePlayer.reset()
      if (
        data === null ||
        (dataType === currentlyPlaying.type && data.index === currentlyPlaying.index)
      ) {
        setCurrentlyPlaying({
          type: null,
          index: -1
        })
        return
      }

      studio.pause()
      if (dataType === CurrentlyPlayingTypes.STEM) {
        // assuming data is RemixableStemMetadata
        const stemStore: IStemStore = data.getStemStore()
        const samples =
          stemStore.type() === StemType.DRUMS
            ? (stemStore as IDrumStemStore).drumSamples.map((e) => e.sampleIndex)
            : [(stemStore as IInstrumentStemStore).melodicSample.sampleIndex]
        alternatePlayer.song.updateTempo(data.tempo)
        alternatePlayer.addStemFromRemix(data)
        ArpRegistry.getByIdsAsync(samples)
          .then((sampleArpMedia: IArpMedia[]) => getMultipleSampleAudioBuffers(sampleArpMedia))
          .then(() => alternatePlayer.play())
      } else if ((dataType === CurrentlyPlayingTypes.SONG, 'Incorrect data type, expected song')) {
        alternatePlayer.loadSongFromArpMedia(data).then(() => alternatePlayer.play())
      }
      setCurrentlyPlaying({
        type: dataType,
        index: data.index
      })
    },
    [currentlyPlaying]
  )

  //   Disallow right click, reset alternatePlayer on close
  useEffect(() => {
    const cancelRightClick = (e) => e.preventDefault()
    document.addEventListener('contextmenu', cancelRightClick)
    return () => {
      document.removeEventListener('contextmenu', cancelRightClick)
      alternatePlayer.reset()
    }
  }, [])

  //   initialize studio and alternatePlayer
  useEffect(() => {
    if (!alternatePlayer.loading) {
      alternatePlayer.initialize(false).catch((err) => console.error(err))
    }
    studio.initialize(true).catch((err) => console.error(err))
  }, [])

  //   set page title, stop audio on page close
  useEffect(() => {
    document.title = 'Arpeggi Studio'
    return () => studio.stop()
  }, [location.pathname])

  return {
    handleHotkeys,
    currentlyPlaying,
    updateCurrentlyPlaying,
    setSideDrawerContent,
    pullOutSideDrawer,
    selectedActivityBarIcon,
    setSelectedActivityBarIcon,
    closeSideDrawer,
    sideDrawerContent,
    selectedTrackContainerHeight,
    instrumentLoopWidthPX,
    zoomIn,
    zoomOut,
    clipboard,
    setClipboard,
    handleSelectedContainerResize
  }
}

export default useStudio
